import { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Divider, Grid, List, ListItem, ListItemText, Typography } from '@mui/material';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import { useParams } from 'react-router-dom';

import Page from '../components/Controls/Page';
import ItemCard from '../components/ItemCard';
import Notification from '../utils/Notification';
import { BaseAPI } from '../utils/Api';
import { getItem } from '../utils/Helper';
import LoadingState from '../components/Controls/LoadingState';
import { isValidToken } from '../utils/Jwt';

const DatasetDetails = () => {
  const { name } = useParams();

  const [loading, setLoading] = useState(false);
  const [datasetInfo, setDatasetInfo] = useState({});
  const [relatedDataset, setRelatedDataset] = useState([]);

  useEffect(() => {
    getDetailsPageInfo();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDetailsPageInfo = () => {
    const getId = getItem(localStorage, 'un') || null;
    setLoading(true);
    BaseAPI.get(`/dataset/details/${name}?user_id=${getId}`)
      .then((res) => {
        setDatasetInfo(res.data.data.details);
        setRelatedDataset(res.data.data.related);
      })
      .catch((err) => {
        Notification(err?.response?.data?.message, 'error');
      })
      .finally(() => setLoading(false));
  };

  const handleWishList = (data) => {
    const getToken = getItem(localStorage, 'at');

    if (localStorage.getItem('at') && isValidToken(getToken)) {
      BaseAPI.post('/dataset/wishlist', { dataset_url: data }, { headers: { Authorization: `Bearer ${getItem(localStorage, 'at')}` } })
        .then(() => {
          getDetailsPageInfo();
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      Notification('Please login first', 'error');
    }
  };

  const handleCart = (data) => {
    const getToken = getItem(localStorage, 'at');

    if (localStorage.getItem('at') && isValidToken(getToken)) {
      BaseAPI.post('/dataset/cartlist', { dataset_url: data }, { headers: { Authorization: `Bearer ${getItem(localStorage, 'at')}` } })
        .then(() => {
          getDetailsPageInfo();
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      Notification('Please login first', 'error');
    }
  };

  return (
    <Page title='Dataset Details'>
      <LoadingState loading={loading} />
      <Box my={3}>
        <Card elevation={0}>
          <CardContent>
            <Grid container spacing={6}>
              <Grid item xs={12} md={6} lg={8}>
                <img
                  src={`${process.env.REACT_APP_s3_cdn}/${process.env.REACT_APP_STAGE}/cover_images/${datasetInfo.image}`}
                  alt='dataset'
                  style={{ borderRadius: 12, height: { sx: '100%', md: '470px' }, width: '100%' }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <Typography variant='h5' mb={2}>
                  {datasetInfo.name}
                </Typography>
                <Box sx={{ display: 'flex', margin: '1em 0', gap: 1 }}>
                  <Typography variant='subtitle1' color='secondary'>
                    ৳&nbsp;{datasetInfo.price - datasetInfo.discount}
                  </Typography>
                  {datasetInfo.discount !== 0 && (
                    <Typography variant='subtitle1' sx={{ textDecoration: 'line-through', color: '#B4B4B4' }}>
                      ৳&nbsp;{datasetInfo.price}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'row-reverse', md: 'row' }, gap: 1 }}>
                  <Button fullWidth variant='contained' disableElevation color={datasetInfo.is_cart ? 'secondary' : 'primary'} onClick={() => handleCart(name)}>
                    {datasetInfo.is_cart ? 'Remove From Cart' : 'Add To Cart'}
                  </Button>
                  <Button
                    fullWidth
                    variant='outlined'
                    color='error'
                    disableElevation
                    onClick={() => handleWishList(name)}
                    endIcon={datasetInfo.is_wish ? <FavoriteRoundedIcon color='error' /> : <FavoriteBorderRoundedIcon />}>
                    {datasetInfo.is_wish ? 'In Wishlist' : 'Add To Wishlist'}
                  </Button>
                </Box>
                <Divider sx={{ mt: 4, mb: 1 }} />
                <List p={0}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary='Data Rows' />
                    <Typography variant='body2' edge='end'>
                      {datasetInfo.data_rows}
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary='Downloaded' />
                    <Typography variant='body2' edge='end'>
                      {datasetInfo.downloads < 10 ? 'Below 10 times' : `${datasetInfo.downloads} times`}
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary='Released' />
                    <Typography variant='body2' edge='end'>
                      {datasetInfo.released}
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary='Last Updated' />
                    <Typography variant='body2' edge='end'>
                      {datasetInfo.last_updated}
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary='Category' />
                    <Typography variant='body2' edge='end'>
                      {datasetInfo.category}
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary='Sold by' />
                    <Typography variant='body2' edge='end'>
                      {datasetInfo.sold_by}
                    </Typography>
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* dataset details */}
      <Card elevation={0}>
        <CardContent>
          <Typography variant='subtitle1' color='secondary' mb={4}>
            Dataset Detail
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div dangerouslySetInnerHTML={{ __html: datasetInfo.details }}></div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* you may also like */}
      <Box mt={6}>
        <Typography variant='h5' align='center' mb={4}>
          You may also like
        </Typography>
        <Grid container spacing={3}>
          {relatedDataset?.map((el, i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <ItemCard
                link={el.url_name}
                image={el.image}
                category={el.category}
                name={el.name}
                price={el.price}
                discount={el.discount}
                rows={el.data_rows}
                handleCart={handleCart}
                handleWishList={handleWishList}
                isCart={el.is_cart}
                isWish={el.is_wish}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Page>
  );
};

export default DatasetDetails;
